import { Block, type Document } from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';

import { BLOCK_DIFF, BLOCK_DIFF_UNMODIFIED } from '../types';
import { diffBlocks } from './diffBlocks';

export interface DiffDocumentsOptions {
    /**
     * If true, consecutive non-modified blocks will be wrapped in a diff-unmodified block.
     */
    wrapUnmodifiedBlocks?: boolean;
}

/**
 * Compute diff between two documents.
 *
 * Diff of documents works by diffing top-level blocks with a certain set of options.
 * then diff the inner content of each leaf blocks.
 */
export function diffDocuments(
    original: Document,
    modified: Document,
    options: DiffDocumentsOptions = {}
): Document {
    const diffed = diffBlocks(original, modified);
    const hasDiff = diffed.some((block) => block.type === BLOCK_DIFF);

    return (
        original
            .merge({
                nodes:
                    hasDiff && options?.wrapUnmodifiedBlocks
                        ? wrapUnmodifiedBlocks(diffed)
                        : diffed,
            })
            // To avoid conflict with keys for texts that are in moved nodes
            .regenerateTextKeys()
    );
}

/**
 * Wrap consecutive non-modified blocks in a diff-unmodified block.
 */
function wrapUnmodifiedBlocks(blocks: List<Block>) {
    const result: Block[] = [];

    let buffer: Block[] = [];

    const flush = () => {
        if (buffer.length > 0) {
            result.push(
                Block.create({
                    type: BLOCK_DIFF_UNMODIFIED,
                    nodes: List.create(chunkUnmodifiedBlock(buffer)),
                })
            );
            buffer = [];
        }
    };

    blocks.forEach((block, index) => {
        const previousBlock = blocks.get(index - 1);
        const nextBlock = blocks.get(index + 1);

        const isDiff = block.type === BLOCK_DIFF;
        const isPreviousDiff = previousBlock?.type === BLOCK_DIFF;
        const isNextDiff = nextBlock?.type === BLOCK_DIFF;

        if (isDiff) {
            flush();
            result.push(block);
        } else {
            if (isPreviousDiff || isNextDiff) {
                // We always display the first non-modified block after and before a diff block.
                flush();
                result.push(block);
            } else {
                buffer.push(block);
            }
        }
    });

    flush();

    return List.create(result);
}

/**
 * Chunk a set of blocks into BLOCK_DIFF_UNMODIFIED that are recursively nested so that the user
 * can click to see the blocks by interval of 5 blocks.
 */
function chunkUnmodifiedBlock(blocks: Block[], maxBlocksPerChunk = 5): Block[] {
    if (blocks.length <= maxBlocksPerChunk) {
        return blocks;
    }

    const before = blocks.slice(0, maxBlocksPerChunk);
    const after = blocks.slice(maxBlocksPerChunk);

    return [
        ...before,
        Block.create({
            type: BLOCK_DIFF_UNMODIFIED,
            nodes: List.create(chunkUnmodifiedBlock(after)),
        }),
    ];
}
